import React, { useState } from "react";
import Page from "components/layouts/Page";
import {
  RentControlTypesFormClient,
  RentControlTypesForm,
  RentControlTypesFormRentControlType,
  RentControlTypesFormLimit,
  ApiException
} from "api";
import RequirePermission from "../RequirePermission";
import { useRemoteData, responseHandlers } from "components/useRemoteData";
import { useApiEndpoint } from "components/useApiEndpoint";
import RentControlTypesFormTable from "components/forms/RentControlTypesFormTable";
import RentControlTypeLimitsFormTable from "components/forms/RentControlTypeLimitsFormTable";
import Heading from "components/controls/Heading";
import Spinner from "components/controls/Spinner";
import useMessageArea from "components/useMessageArea";
import PrimaryButton from "components/controls/PrimaryButton";
import UnsavedChangesPrompt from "components/controls/UnsavedChangesPrompt";
import Spacer from "components/controls/Spacer";
import Section from "components/controls/Section";
import ContentArea from "components/layouts/ContentArea";
import listUtils from "utils/listUtils";
import newRowUtils from "utils/newRowUtils";
import VisibilityToggle from "components/controls/VisibilityToggle";
import RegulationsMenu from "../RegulationsMenu";

interface PageState {
  form: RentControlTypesForm | undefined;
  hasFormChanged: boolean;
}

const RentControlTypesFormPage: React.FC = function () {
  const [page, setPage] = useState<PageState>({
    form: undefined,
    hasFormChanged: false
  });

  const [
    messageAreaState,
    setMessageBasedOnForm,
    setMessageBasedOnApiException
  ] = useMessageArea();

  const setForm = function (
    form: RentControlTypesForm | null,
    hasFormChanged: boolean = true
  ) {
    if (!form) {
      return;
    }

    setPage({
      ...page,
      form: {
        ...form
      },
      hasFormChanged: hasFormChanged
    });
  };

  const [dataState] = useRemoteData(
    useApiEndpoint(RentControlTypesFormClient, "getRentControlTypesForm"),
    [],
    {
      autoFetch: true,
      onChange: (form) => {
        setForm(form, false);
      }
    }
  );

  const [submitState, submit] = useRemoteData(
    useApiEndpoint(RentControlTypesFormClient, "saveRentControlTypesForm"),
    [page.form || {}],
    {
      onChange: (form) => {
        setMessageBasedOnForm(form);
        setForm(form, false);
      },
      onError: (error: ApiException) => {
        responseHandlers.use({
          "400": (ex: ApiException) => {
            const form = ex.result;
            setMessageBasedOnForm(form);
            setForm(form);
          },
          _: (ex) => {
            setMessageBasedOnApiException(ex);
          }
        })(error);
      }
    }
  );

  const spinner = (
    <Spinner isVisible={dataState.isFetching || submitState.isFetching} />
  );

  if (page.form == null && (dataState.isFetching || submitState.isFetching)) {
    return spinner;
  } else if (page.form == null) {
    return messageAreaState.messageArea;
  }

  const form = page.form;
  const isEditEnabled = !!(
    form.pageConfiguration && form.pageConfiguration.isEditEnabled
  );

  const onRentControlTypesChange = (
    rentControlTypes: RentControlTypesFormRentControlType[]
  ) => {
    setForm({
      ...form,
      rentControlTypes: rentControlTypes
    });
  };

  const addRentControlTypeRow = function (): void {
    onRentControlTypesChange(
      (form.rentControlTypes || []).concat({
        key: newRowUtils.nextTemporaryKey(),
        isChanged: true
      })
    );
  };

  /*
   * Only an unsaved rent control can be removed, so any allowed increases referencing it are unsaved too and are
   * removed with it rather than being left pointing at a rent control that no longer exists.
   */
  const removeRentControlTypeRow = function (
    rowToRemove: RentControlTypesFormRentControlType
  ): void {
    setForm({
      ...form,
      rentControlTypes: listUtils.copyListWithItemRemoved(
        form.rentControlTypes || [],
        rowToRemove
      ),
      limits: (form.limits || []).filter(
        (limit) =>
          limit.selectedRentControlType?.key !== rowToRemove.key
      )
    });
  };

  const onLimitsChange = (
    limits: RentControlTypesFormLimit[]
  ) => {
    setForm({
      ...form,
      limits: limits
    });
  };

  const addIncreaseLimitRow = function (): void {
    onLimitsChange(
      (form.limits || []).concat({
        key: newRowUtils.nextTemporaryKey(),
        isChanged: true
      })
    );
  };

  const removeIncreaseLimitRow = function (
    rowToRemove: RentControlTypesFormLimit
  ): void {
    onLimitsChange(
      listUtils.copyListWithItemRemoved(form.limits || [], rowToRemove)
    );
  };

  const isPageDirty = page.hasFormChanged;

  const actionArea = (
    <div style={{ display: "flex", alignItems: "center" }}>
      <UnsavedChangesPrompt isVisible={isPageDirty} />
      <Spacer orientation="h" size="xl" />
      <VisibilityToggle isVisible={isEditEnabled}>
        <PrimaryButton
          disabled={submitState.isFetching}
          onClick={() => submit([page.form])}
          title="SAVE"
        />
      </VisibilityToggle>
    </div>
  );

  return (
    <Page menu={<RegulationsMenu actionArea={actionArea} />}>
      <RequirePermission
        permissions={["canViewBatchListForm"]}
        accessDenied="page"
      >
        {spinner}
        {messageAreaState.messageArea}
        <Section title="RENT CONTROL TYPES" />
        <Spacer size="l" orientation="v" />

        <ContentArea columnSize={"12"}>
          <Heading level="3">Rent controls</Heading>
          <Spacer size="s" orientation="v" />
          <RentControlTypesFormTable
            rentControlTypes={form.rentControlTypes}
            onChange={onRentControlTypesChange}
            onAddRowAction={isEditEnabled ? addRentControlTypeRow : undefined}
            onRemoveRowAction={removeRentControlTypeRow}
            canRemoveRow={(row) =>
              newRowUtils.isUnsaved(row.key) || !row.isUsedByRentControlDefaults
            }
            pageConfiguration={form.pageConfiguration}
          />
        </ContentArea>

        <Spacer size="l" orientation="v" />

        <ContentArea columnSize={"12"}>
          <Heading level="3">Allowed increases</Heading>
          <Spacer size="s" orientation="v" />
          <RentControlTypeLimitsFormTable
            limits={form.limits}
            rentControlTypes={form.rentControlTypes || []}
            onChange={onLimitsChange}
            onAddRowAction={isEditEnabled ? addIncreaseLimitRow : undefined}
            onRemoveRowAction={removeIncreaseLimitRow}
            pageConfiguration={form.pageConfiguration}
          />
        </ContentArea>
      </RequirePermission>
    </Page>
  );
};

export default RentControlTypesFormPage;
